import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { CATEGORIES, formatLabel } from "@/lib/api";
import EntryCard from "@/components/EntryCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Quote, FileCheck, ScrollText } from "lucide-react";
import { toast } from "sonner";

export default function Home() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/entries", { params: { limit: 6, sort: "date_desc" } }).then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await api.post("/subscribers", { email });
      if (res.data.was_existing) {
        toast.message("You're already on the list.", { description: "We'll only send you what we'd want to read ourselves." });
      } else {
        toast.success("Subscribed. We'll send updates when major actions are documented.");
      }
      setEmail("");
    } catch { toast.error("Could not subscribe. Check your email."); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-white border-b border-black/15 grain">
        <div className="max-w-[1400px] mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-12 gap-10 relative z-10">
          <div className="lg:col-span-7">
            <div className="overline mb-4" data-testid="hero-overline">Vol. I · Issue No. 01 · Public Record</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              The receipts
              <br />
              of <span className="bg-black text-white px-3 inline-block">rolled-back</span>
              <br />
              women's rights.
            </h1>
            <p className="mt-6 text-lg text-[#222] max-w-xl leading-relaxed">
              An independent ledger of every government action — federal and state —
              shaping women's rights in the United States. Every entry has a citation.
              Every citation has a receipt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/tracker">
                <Button data-testid="hero-explore-btn" className="rounded-none bg-black hover:bg-[#333] text-white h-12 px-6 text-base">
                  Explore the tracker <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/timeline">
                <Button data-testid="hero-timeline-btn" variant="outline" className="rounded-none border-black h-12 px-6 text-base">
                  See the timeline
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <img
              src="https://images.unsplash.com/photo-1768341166201-6f0d8325860f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjYXBpdG9sJTIwYnVpbGRpbmclMjB3YXNoaW5ndG9uJTIwYmxhY2slMjBhbmQlMjB3aGl0ZXxlbnwwfHx8fDE3Nzg0MjkxNzN8MA&ixlib=rb-4.1.0&q=85"
              alt="U.S. Capitol building, black and white"
              className="w-full h-[420px] object-cover grayscale"
            />
            <div className="absolute -bottom-6 -left-6 bg-black text-white p-5 max-w-xs hidden md:block">
              <Quote className="w-5 h-5 mb-2 opacity-60" />
              <div className="font-display text-lg leading-snug">
                "What gets measured gets defended."
              </div>
              <div className="font-mono-plex text-[11px] tracking-[0.25em] uppercase text-white/60 mt-3">— Project ethos</div>
            </div>
          </div>
        </div>

        {/* Stat strip */}
        {stats && (
          <div className="border-t border-black/15 bg-[#F9F9F9]">
            <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-2 md:grid-cols-5 divide-x divide-black/10">
              <Stat data-testid="stat-total" label="Documented Actions" value={stats.total_entries} />
              <Stat data-testid="stat-receipts" label="Citations on File" value={stats.total_citations} />
              <Stat data-testid="stat-restricts" label="Restrictive Actions" value={stats.by_impact.restricts} accent="alert" />
              <Stat data-testid="stat-protects" label="Protective Actions" value={stats.by_impact.protects} accent="success" />
              <Stat data-testid="stat-jurisdictions" label="Federal / State" value={`${stats.by_level.federal} / ${stats.by_level.state}`} />
            </div>
          </div>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="flex justify-between items-end mb-8 border-b border-black/15 pb-4">
          <div>
            <div className="overline">/by category</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-2">Eight fronts of the rollback</h2>
          </div>
          <Link to="/categories" className="hidden sm:block text-sm font-mono-plex uppercase tracking-wider underline underline-offset-4">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.slug}
              to={`/categories/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group block bg-white border border-black/15 p-6 hover:bg-black hover:text-white transition-colors duration-150 min-h-[200px] flex flex-col justify-between"
            >
              <div>
                <div className="font-mono-plex text-[11px] tracking-[0.2em] uppercase opacity-60">№ {String(i + 1).padStart(2, "0")}</div>
                <div className="font-display text-2xl font-bold mt-3 leading-tight">{c.label}</div>
              </div>
              <div className="text-sm leading-relaxed mt-4 opacity-80">{c.blurb}</div>
              <div className="mt-4 text-xs font-mono-plex uppercase tracking-wider flex items-center gap-2">
                Browse <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* RECENT ENTRIES */}
      <section className="bg-white border-y border-black/15">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <div className="flex justify-between items-end mb-8 border-b border-black/15 pb-4">
            <div>
              <div className="overline">/latest entries</div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-2">Recently documented</h2>
            </div>
            <Link to="/tracker" className="hidden sm:block text-sm font-mono-plex uppercase tracking-wider underline underline-offset-4">
              All entries →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recent.map((e) => <EntryCard key={e.id} entry={e} />)}
          </div>
        </div>
      </section>

      {/* METHODOLOGY TEASER */}
      <section className="max-w-[1400px] mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline">/methodology</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-2">No claim without a receipt.</h2>
          <p className="mt-5 text-[#222] leading-relaxed">
            Every entry is built on linked, verifiable source material — government documents,
            court opinions, agency rulings, and reporting from established outlets. The evidence
            is the point. Browse the methodology to see how entries are vetted and structured.
          </p>
          <div className="mt-6 flex gap-3">
            <Link to="/about"><Button data-testid="learn-method-btn" variant="outline" className="rounded-none border-black h-11">Read methodology</Button></Link>
            <Link to="/submit"><Button data-testid="home-submit-btn" className="rounded-none bg-black text-white h-11">Submit a tip</Button></Link>
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Pillar icon={<ScrollText className="w-6 h-6" />} title="Primary documents" body="Court filings, bill text, agency rules, and official statements come first." />
          <Pillar icon={<FileCheck className="w-6 h-6" />} title="Cross-referenced" body="Each action is verified against at least two independent sources where possible." />
          <Pillar icon={<Quote className="w-6 h-6" />} title="Direct quotes" body="When language matters, we pull verbatim quotes — not paraphrases." />
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-14 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-plex text-xs uppercase tracking-[0.25em] text-white/60">/the dispatch</div>
            <h3 className="font-display text-3xl md:text-4xl font-black mt-2">Get the receipts in your inbox.</h3>
            <p className="text-white/80 mt-3 max-w-xl">
              Periodic, no-fluff briefings on newly documented actions, structured for citation and reuse.
            </p>
          </div>
          <form className="lg:col-span-5 flex gap-2" onSubmit={subscribe}>
            <Input
              data-testid="newsletter-email-input"
              type="email"
              required
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none h-12 bg-white text-black border-white"
            />
            <Button data-testid="newsletter-submit-btn" className="rounded-none bg-white text-black hover:bg-[#EAEAEA] h-12 px-6">
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, accent, ...rest }) {
  let accentColor = "text-black";
  if (accent === "alert") accentColor = "text-[#D32F2F]";
  else if (accent === "success") accentColor = "text-[#008A00]";
  return (
    <div className="px-6 py-7" {...rest}>
      <div className="font-mono-plex text-[11px] tracking-[0.2em] uppercase text-[#555]">{label}</div>
      <div className={`font-display text-4xl lg:text-5xl font-black mt-2 ${accentColor}`}>{value}</div>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="border border-black/15 bg-white p-5">
      <div className="text-black">{icon}</div>
      <div className="font-display text-xl font-bold mt-3">{title}</div>
      <p className="text-sm text-[#333] mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
